import angular from 'angular';

import { stringToLines } from 'src/app/shared/string-utils';
import { validate as validateUuid } from 'uuid';

import {
    Printers,
    Sales,
    SalesDocuments,
} from 'tilby-models'

import {
    DailyClosingConfig,
    DailyClosingPlugin,
    DailyClosingResult,
    DocumentPrinterOptions,
    DocumentPrintHook,
    IsPrintableOptions,
    OptionsPrint,
} from 'src/app/shared/model/document-printer.model';

import { FiscalUtilsService } from 'app/modules/core/service/fiscal-utils/fiscal-utils';
import { FiscalPrintersService } from 'app/modules/printers/service/fiscal-printers/fiscal-printers';

import {
    ConfigurationManagerService,
    EntityManagerService,
    EnvironmentInfoService,
    ErrorsLoggerService,
    UserActiveSessionManagerService
} from 'src/app/core';

import { LeanPMSPayment } from 'app/modules/digital-payments/service/lean-pms/lean-pms';
import { SpiaggeItService } from 'app/modules/digital-payments/service/spiagge-it/spiagge-it';
import { EricsoftPMSService } from 'app/modules/digital-payments/service/ericsoft-pms/ericsoft-pms';
import { BeddyService } from 'app/modules/digital-payments/service/beddy/beddy';
import { BedzzleService } from 'app/modules/digital-payments/service/bedzzle/bedzzle';

import {
    deepClone,
    keyBy
} from 'src/app/shared/utils';

import {
    PrinterDriverInfo
} from 'src/app/shared/model/it-fiscal-printer.model';

import {
    SaleUtilsService
} from 'src/app/features';

import {
    SalesCashregister
} from 'src/app/shared/model/cashregister.model';

import {
    PrintLayoutBuilder
} from 'src/app/features/printers/drivers';

export class DocumentPrinterService {
    private paymentPlugins: DocumentPrintHook[];
    private pmsPlugins: DocumentPrintHook[];
    private documentPlugins: { [key: string]: DocumentPrintHook[] };
    private dailyClosingPlugins: DailyClosingPlugin[];

    static $inject = [
        "$translate",
        "$rootScope",
        "util",
        "alertDialog",
        "errorsLogger",
        "FiscalPrinters",
        "fiscalUtils",
        "NoFiscalPrinters",
        "checkManager",
        "entityManager",
        "environmentInfo",
        "saleUtils",
        "userActiveSession",
        "eInvoiceSend",
        "ddtWine",
        "prepaidSale",
        "satispay",
        "bedzzle",
        "beddy",
        "leanPMS",
        "spiaggeIt",
        "ericsoftPMS",
        "fidelitySalePoints",
        "GiftCardSale",
        "invoicePlugin",
        "eInvoicePlugin",
        "shippingInvoice",
        "NonFiscalDocumentHook",
        "VneCashdrawer",
        "campgestPMS",
        "zPMSConnector"
    ];

    constructor(
        private readonly $translate: any,
        private readonly $rootScope: any,
        private readonly util: any,
        private readonly alertDialog: any,
        private readonly errorsLogger: ErrorsLoggerService,
        private readonly FiscalPrinters: FiscalPrintersService,
        private readonly fiscalUtils: FiscalUtilsService,
        private readonly NoFiscalPrinters: any,
        private readonly configurationManagerService: ConfigurationManagerService,
        private readonly entityManager: EntityManagerService,
        private readonly environmentInfo: EnvironmentInfoService,
        private readonly saleUtilsService: SaleUtilsService,
        private readonly userActiveSession: UserActiveSessionManagerService,
        private readonly eInvoiceSend: any,
        ddtWine: DocumentPrintHook,
        prepaidSale: DocumentPrintHook,
        satispay: DocumentPrintHook,
        bedzzle: BedzzleService,
        beddy: BeddyService,
        leanPMS: LeanPMSPayment,
        spiaggeIt: SpiaggeItService,
        ericsoftPMS: EricsoftPMSService,
        fidelitySalePoints: DocumentPrintHook,
        GiftCardSale: DocumentPrintHook,
        invoicePlugin: DocumentPrintHook,
        eInvoicePlugin: DocumentPrintHook,
        shippingInvoice: DocumentPrintHook,
        NonFiscalDocumentHook: DocumentPrintHook,
        VneCashdrawer: DocumentPrintHook,
        campgestPMS: DocumentPrintHook,
        zPMSConnector: DocumentPrintHook
    ) {

        this.paymentPlugins = [
            prepaidSale,
            GiftCardSale,
            satispay,
            VneCashdrawer,
        ];

        this.pmsPlugins = [
            bedzzle,
            beddy,
            leanPMS,
            spiaggeIt,
            ericsoftPMS,
            campgestPMS,
            zPMSConnector
        ];

        this.documentPlugins = {
            fiscal_receipt: [
                ddtWine,
                ...this.paymentPlugins,
                ...this.pmsPlugins,
                fidelitySalePoints,
            ],
            invoice: [
                invoicePlugin,
                ...this.paymentPlugins,
            ],
            receipt_invoice: [
                invoicePlugin,
                satispay,
            ],
            summary_invoice: [
                invoicePlugin,
            ],
            shipping_invoice: [
                shippingInvoice,
                ...this.paymentPlugins,
            ],
            e_invoice: [
                eInvoicePlugin,
                ...this.paymentPlugins,
                ...this.pmsPlugins,
            ],
            summary_e_rc: [
                eInvoicePlugin,
                ...this.paymentPlugins,
                ...this.pmsPlugins,
            ],
            summary_e_nrc: [
                eInvoicePlugin,
                ...this.pmsPlugins,
            ],
            generic_receipt: [
                NonFiscalDocumentHook, 
                ...this.paymentPlugins, 
                ...this.pmsPlugins, 
                fidelitySalePoints,
            ],
            generic_invoice: [
                invoicePlugin,
                NonFiscalDocumentHook,
                ...this.paymentPlugins,
                ...this.pmsPlugins,
                fidelitySalePoints,
            ],
            generic_document: [
                NonFiscalDocumentHook,
                ...this.paymentPlugins,
                ...this.pmsPlugins,
                fidelitySalePoints,
            ],
            credit_note: [
                NonFiscalDocumentHook,
                ...this.paymentPlugins,
                ...this.pmsPlugins,
                fidelitySalePoints,
            ],
        };

        this.dailyClosingPlugins = [
            leanPMS
        ];
    }

    private getDocumentType(printerDocumentData?: DocumentPrinterOptions) {
        return printerDocumentData?.document_type?.id;
    }

    private checkPrinterDocumentData(printerDocumentData?: DocumentPrinterOptions): asserts printerDocumentData is DocumentPrinterOptions {
        if (!printerDocumentData || typeof printerDocumentData !== 'object') {
            throw 'MISSING_PRINTER_DOCUMENT_DATA';
        }

        if (!printerDocumentData.printer || typeof printerDocumentData.printer !== 'object') {
            throw 'MISSING_PRINTER';
        }

        if (!printerDocumentData.options) {
            printerDocumentData.options = {};
        }
    }

    private getPrinterColumns(printer: Printers): number {
        //Get Printer Columns
        let printerCols = printer.columns;

        if (!printerCols) {
            switch (printer.driver) {
                case 'axon':
                case 'axon_g100':
                case 'rch':
                    printerCols = 48;
                    break;
                case 'epson':
                    printerCols = 46;
                    break;
                default:
                    printerCols = 46;
            }
        }

        return printerCols;
    }

    private reflowDocumentColumns(documentToPrint: undefined | string | string[], printer: Printers): string[] {
        //Split document into multiple lines if necessary and remove white spaces at beginning/end of each line
        if (!documentToPrint) {
            return [];
        }

        let doc: string[] = (Array.isArray(documentToPrint) ? documentToPrint : documentToPrint.split('\n')).map(line => {
            line = line.trimEnd();

            //If the trimmed line is empty, add an empty space to keep line breaks intact
            if (!line) {
                line = ' ';
            }
            return line;
        });

        //Get number of columns
        const documentCols = Math.max(...doc.map(line => line.length));

        //Get Printer Columns
        const printerCols = this.getPrinterColumns(printer);

        //Reflow lines if the printer hasn't enough columns
        if (documentCols > printerCols) {
            doc = stringToLines(doc, printerCols);
        }

        return doc;
    }

    private async handleDocumentPlugins(clonedSale: Sales, pluginData: Record<number, any>, printerDocumentData: DocumentPrinterOptions) {
        const result: { tail: string[]; otherDocuments: string[][]; options: any; } = {
            tail: [],
            otherDocuments: [],
            options: {}
        };

        const documentType = this.getDocumentType(printerDocumentData);

        if (!documentType || !this.documentPlugins[documentType]) {
            return {
                tail: [],
                otherDocuments: [],
                options: {}
            };
        }

        for (let i = 0; i < this.documentPlugins[documentType].length; i++) {
            const plugin = this.documentPlugins[documentType][i];

            if (plugin.isEnabled() && typeof plugin.getData === 'function') {
                let pluginResult = await plugin.getData(clonedSale, printerDocumentData, pluginData[i]);

                if (!pluginResult || typeof pluginResult !== 'object') {
                    pluginResult = {};
                }

                if (Array.isArray(pluginResult.tail)) {
                    result.tail.push('');
                    result.tail.push(...pluginResult.tail);
                    result.tail.push('');
                }

                if (Array.isArray(pluginResult.otherDocument)) {
                    result.otherDocuments.push(pluginResult.otherDocument);
                }

                if (pluginResult.options && typeof pluginResult.options === 'object') {
                    Object.assign(result.options, pluginResult.options);
                }
            }
        }

        return {
            tail: result.tail.join('\n'),
            otherDocuments: result.otherDocuments,
            options: result.options
        };
    }

    private async handlePrintHooks(printHookName: 'prePrintHook' | 'postPrintHook' | 'printFailHook', clonedSale: Sales, pluginData: Record<number, any>, printerDocumentData: DocumentPrinterOptions) {
        const documentType = this.getDocumentType(printerDocumentData);
        const plugins = this.documentPlugins[documentType!] || [];

        for (const [i, plugin] of plugins.entries()) {
            if (plugin.isEnabled() && typeof plugin[printHookName] === 'function') {
                const data = await plugin[printHookName]!(clonedSale, printerDocumentData, pluginData[i]);

                if (data) {
                    pluginData[i] = data;
                }
            }
        }
    }

    /**
     * Clean up a sale, removing void price changes from the sale and its items.
     * Modifies the sale in place.
     * 
     * @param sale The sale to clean up.
     * @returns The cleaned up sale.
     */
    private cleanupSale<T extends Sales | SalesCashregister>(sale: T): T {
        sale.price_changes = sale.price_changes?.filter(pc => pc.amount !== 0) || [];

        for (const saleItem of sale.sale_items || []) {
            saleItem.price_changes = saleItem.price_changes?.filter(pc => pc.amount !== 0) || [];
        }

        return sale;
    }

    private async checkSaleBeforePrint(sale: Sales, printerDocumentData: DocumentPrinterOptions, options?: IsPrintableOptions) {
        if (!sale || typeof sale !== 'object' || !printerDocumentData?.document_type) {
            throw 'DOCUMENT_TYPE_NOT_FOUND';
        }
        //Check customer for certain document types
        if (this.configurationManagerService.getSetting('cashregister.fiscalprinter.must_select_customer') && !sale?.sale_customer) {
            throw 'CUSTOMER_MISSING';
        }

        const documentType = this.getDocumentType(printerDocumentData);
        const isEInvoice = ['summary_e_rc', 'summary_e_nrc', 'e_invoice'].includes(documentType as string);
        //Check for e-invoice document type if sale is an e-invoice
        if (sale.e_invoice != null && !isEInvoice) {
            throw 'E_INVOICE_WRONG_DOCTYPE';
        }

        //Check if there are items with a positive quantity when the amount is negative
        if (sale.final_amount! < 0 && sale.sale_items?.some(saleItem => saleItem.quantity > 0)) {
            throw 'INCONGRUITY_ON_SALE';
        }

        //Checks specific to RT printers
        if (printerDocumentData.printer?.type === 'rt' && !isEInvoice) {
            const taxSum = Object.values(this.fiscalUtils.extractTax(sale)).reduce((sum, item) => sum + item.tax, 0);

            if (taxSum < 0 && sale.sale_items?.some(saleItem => saleItem.quantity > 0)) {
                throw 'NEGATIVE_TAX_ON_RT';
            }

            //Check for 0 VAT items that have not an exemption (only RT printers)
            const departmentsById = await this.entityManager.departments.fetchCollectionOffline().then(deps => keyBy(deps, d => d.id));

            const hasZeroVatItem = sale.sale_items?.some(saleItem => {
                const vatData = departmentsById[saleItem.department_id]?.vat;

                return vatData?.value === 0 && !(vatData.id === 0 || vatData.id >= 10);
            });

            if (hasZeroVatItem) {
                throw 'ZERO_VAT_ITEMS_ON_RT';
            }
        }

        if (Array.isArray(this.documentPlugins[documentType as string])) {
            for (const plugin of this.documentPlugins[documentType as string]) {
                if (plugin.isEnabled() && typeof plugin.isPrintable === 'function') {
                    await plugin.isPrintable(sale, printerDocumentData, options);
                }
            }
        }
    }

    private async preparePrinterDriver(printer: Printers) {
        switch (printer.type) {
            case 'fiscal': case 'rt':
                return this.FiscalPrinters.preparePrinterDriver(printer.id!);
            case 'receipt':
                return { printer: printer } as PrinterDriverInfo;
            default:
                throw 'INVALID_PRINTER_TYPE';
        }
    }

    private async checkDriverCapability(printerData: PrinterDriverInfo, documentTypeId: string) {
        switch (printerData.printer.type) {
            case 'fiscal': case 'rt':
                return this.FiscalPrinters.checkDriverCapability(printerData, documentTypeId);
            case 'receipt':
                return true;
            default:
                throw 'INVALID_PRINTER_TYPE';
        }
    }

    public async checkSale(sale: Sales, printerDocumentData: DocumentPrinterOptions) {
        return this.checkSaleBeforePrint(sale, printerDocumentData);
    }

    public async isPrinterReachable(printer: Printers) {
        if (!printer) {
            throw 'MISSING_PRINTER';
        }

        switch (printer.type) {
            case 'fiscal': case 'rt':
                return this.FiscalPrinters.isReachable(printer);
            case 'receipt':
                return this.NoFiscalPrinters.isReachable(printer);
            default:
                throw 'INVALID_PRINTER_TYPE';
        }
    }

    public isPrinterUsable(printer?: Printers): boolean {
        switch (printer?.driver) {
            case 'custom': case 'axon': case 'axon_g100': case 'escpos': case 'dtr':
                return this.environmentInfo.canUseTcpSockets();
            case 'epos': case 'epson': case 'rch': case 'ditron':
                return true;
            default: //Unknown driver
                return false;
        }
    }

    public async openCashDrawer(printerDocumentData?: DocumentPrinterOptions) {
        this.checkPrinterDocumentData(printerDocumentData);

        this.$rootScope.$broadcast('drawer-open');

        switch (printerDocumentData.printer.type) {
            case 'fiscal': case 'rt':
                return this.FiscalPrinters.openCashDrawer(printerDocumentData);
            case 'receipt':
                return this.NoFiscalPrinters.openCashDrawer(printerDocumentData);
            default:
                throw 'INVALID_PRINTER_TYPE';
        }
    }

    public async displayText(printer: Printers, textLines: string[]) {
        if (!this.isPrinterUsable(printer)) {
            throw 'UNSUPPORTED_ENVIRONMENT';
        }

        switch (printer.type) {
            case 'fiscal': case 'rt':
                return this.FiscalPrinters.displayText(printer, textLines);
            case 'receipt':
                return this.NoFiscalPrinters.displayText(printer, textLines);
            default:
                throw 'INVALID_PRINTER_TYPE';
        }
    }

    public async printNonFiscalSale(sale: Sales, printer: Printers) {
        const localSale = structuredClone(sale);

        switch (printer.type) {
            case 'fiscal': case 'rt':
                return this.FiscalPrinters.printNonFiscalSale(localSale, printer);
            case 'receipt': case 'nonfiscal':
                return this.NoFiscalPrinters.printNonFiscalSale(localSale, printer);
            default:
                throw 'INVALID_PRINTER_TYPE';
        }
    }

    public async dailyClosing(printer: Printers, data?: DailyClosingConfig) {
        if (!printer || typeof printer !== 'object') {
            throw 'MISSING_PRINTER';
        }

        if (!data || typeof data !== 'object') {
            data = {};
        }

        const printerId = printer.id;
        const safetyTimeout = (printer.driver || '') === 'ditron'? 300: 60;

        this.$rootScope.$broadcast('daily-closing', { status: 'IN_PROGRESS', printerId: printerId, customTimeout: safetyTimeout });

        const doDailyClosing = async () => {
            switch (printer.type) {
                case 'fiscal': case 'rt':
                    return this.FiscalPrinters.dailyClosing(printer, data);
                case 'receipt':
                    return <Promise<DailyClosingResult>>this.NoFiscalPrinters.dailyClosing(printer, data);
                default:
                    throw 'INVALID_PRINTER_TYPE';
            }
        };

        const { status, errors } = await doDailyClosing();

        if (status === 'COMPLETED') {
            for (const plugin of this.dailyClosingPlugins) {
                if (plugin.isDailyClosingEnabled() && typeof (plugin.dailyClosing) === 'function') {
                    try {
                        await plugin.dailyClosing(printer, data);
                    } catch (error: any) {
                        errors.push(error);
                    }
                }
            }
        }

        this.$rootScope.$broadcast('daily-closing', { status: status, errors: errors, printerId: printerId });
    }

    public async printFreeNonFiscal(documentToPrint: string | string[], printerId?: number, options?: any) {
        if (!options || typeof options !== 'object') {
            options = {};
        }

        const printer = await this.entityManager.printers.fetchOneOffline(printerId!);

        if (!printer) {
            throw 'PRINTERS_NOT_FOUND';
        }

        const doc = this.reflowDocumentColumns(documentToPrint, printer);

        switch (printer.type) {
            case 'fiscal': case 'rt':
                return this.FiscalPrinters.printFreeNonFiscal(doc, printer, options);
            case 'receipt':
                return this.NoFiscalPrinters.printFreeNonFiscal(doc, printer, options);
            default:
                throw 'INVALID_PRINTER_TYPE';
        }
    }

    public async printFreeDocumentLayout(documentToPrint: PrintLayoutBuilder, printerId?: number, options?: any) {
        if (!options || typeof options !== 'object') {
            options = {};
        }

        if(!printerId) {
            throw 'PRINTERS_NOT_FOUND';
        }

        const printer = await this.entityManager.printers.fetchOneOffline(printerId);

        if (!printer) {
            throw 'PRINTERS_NOT_FOUND';
        }

        switch (printer.type) {
            case 'fiscal': case 'rt':
                return this.FiscalPrinters.printFreeDocumentLayout(documentToPrint, printerId, options);
            case 'receipt':
                return this.NoFiscalPrinters.printFreeDocumentLayout(documentToPrint, printer, options);
            default:
                throw 'INVALID_PRINTER_TYPE';
        }
    }

    public async printSignatureCopy(sale: Sales, printerId?: number) {
        const documentBlackList = new Set([
            'commercial_doc_signed',
            'e_invoice',
            'summary_e_rc',
            'summary_e_nrc',
            'fiscal_provider',
            'attachment',
            'expense_report',
        ]);

        const payments = await this.entityManager.paymentMethods.fetchCollectionOffline({ print_signature_copy: true })
            .then(pm => keyBy(pm, p => p.id));

        if (!sale.payments?.some(payment => payments[payment.payment_method_id])) {
            return;
        }

        const documentToPrint = sale.sale_documents?.find(doc => doc.document_content && !documentBlackList.has(doc.document_type));

        if (!documentToPrint?.document_content) {
            return;
        }

        const printer = await this.entityManager.printers.fetchOneOffline(printerId!);

        if (!printer) {
            throw 'PRINTERS_NOT_FOUND';
        }

        const printerColumns = this.getPrinterColumns(printer);

        const documentContent = [
            ...documentToPrint.document_content!.split('\n'),
            '',
            this.$translate.instant('PRINTERS.DETAIL.SIGNATURE_FIELD'),
            '',
            '',
            '_ '.repeat(printerColumns).slice(0, printerColumns)
        ];

        return this.printFreeNonFiscal(documentContent, printerId);
    }

    public async reprintDocument(documentToReprint: SalesDocuments, referenceSale: Sales, printerId: number | 'dummy_receipt') {
        const printer = printerId === 'dummy_receipt'
            ? {
                type: 'receipt',
                name: 'Dummy Receipt Printer',
                driver: 'epos',
                ip_address: '0.0.0.0',
                id: undefined
            }
            : await this.entityManager.printers.fetchOneOffline(printerId);

        if (!printer) {
            throw 'PRINTERS_NOT_FOUND';
        }

        switch (printer.type) {
            case 'fiscal': case 'rt':
                const documentToPrint = this.reflowDocumentColumns(documentToReprint.document_content!, printer);

                try {
                    await this.FiscalPrinters.printFreeNonFiscal(documentToPrint, printer);

                    return { document_content: documentToPrint };
                } catch (error) {
                    throw error;
                }
            case 'receipt':
                return this.NoFiscalPrinters.reprintDocument(documentToReprint, referenceSale, printer);
            default:
                throw 'INVALID_PRINTER_TYPE';
        }
    }

    public async printDocument(origSale: Sales | SalesCashregister, printerDocumentData: DocumentPrinterOptions) {
        let pluginData: Record<number, any> = {};

        if (!printerDocumentData.document_type) {
            throw 'DOCUMENT_TYPE_NOT_FOUND';
        }

        //Work on a cloned sale, then replace the original with the clone before returning it
        const clonedSale = this.cleanupSale(structuredClone(origSale));

        await this.checkSaleBeforePrint(clonedSale, printerDocumentData, { skip_warnings: true });

        const printerData = await this.preparePrinterDriver(printerDocumentData.printer);

        if (!this.checkDriverCapability(printerData, printerDocumentData.document_type.id)) {
            this.alertDialog.show(this.$translate.instant('PRINTERS.FISCAL.FUNCTION_NOT_AVAILABLE'));
            throw 'FUNCTION_NOT_AVAILABLE';
        }

        let canOpenCashDrawer = this.configurationManagerService.isFunctionEnabledOptout('cashregister.fiscalprinter.can_open_cash_drawer');

        if (canOpenCashDrawer) {
            //Find payment methods used in the sales and disable cash drawer opening if all of the payments have it disabled with the flag disable_open_cash_drawer
            const usedPayments = new Set(origSale.payments?.map(payment => payment.payment_method_id) || []);
            const paymentMethods = await this.entityManager.paymentMethods.fetchCollectionOffline({ id_in: [...usedPayments] });

            if (paymentMethods.length && paymentMethods.every(paymentMethod => paymentMethod.disable_open_cash_drawer)) {
                canOpenCashDrawer = false;
            }
        }

        let optionsPrint: OptionsPrint = {
            can_open_cash_drawer: canOpenCashDrawer,
            print_details: !(printerDocumentData.options?.printOnlyDepartments),
            e_receipt: !!printerDocumentData.options?.eReceipt,
            tail: printerDocumentData.tail
        };

        let departmentItemName;

        //Check if the sale is a dutch treat with split items. If so, force disable print_details
        if ('_split_sale_data' in clonedSale && clonedSale._split_sale_data?.split_mode) {
            optionsPrint.print_details = false;
            departmentItemName = this.$translate.instant('CASHREGISTER.SPLIT_SALE.DEFAULT_ITEM_NAME');
        }

        const receiptUrl = this.configurationManagerService.getPreference('fiscalprinter.qr_code_url');

        if (receiptUrl) {
            const shopName = this.userActiveSession.getSession()?.shop.name!;

            const saleId = validateUuid(`${clonedSale.id}`) ? null : clonedSale.id;
            const saleUuid = validateUuid(clonedSale.uuid!) ? clonedSale.uuid : null;

            const finalAmount = clonedSale.final_amount;
            const saleNotes = (clonedSale.notes || '').slice(0, 30);

            let initialSeparator = receiptUrl.includes('?') ? '&' : '?';

            optionsPrint.qr_code_url = `${receiptUrl}${initialSeparator}shop_name=${encodeURIComponent(shopName)}${saleId ? `&id=${encodeURIComponent(saleId)}` : ''}${saleUuid ? `&uuid=${encodeURIComponent(saleUuid)}` : ''}&final_amount=${finalAmount}${saleNotes ? `&notes=${encodeURIComponent(saleNotes)}` : ''}`;
            optionsPrint.qr_code_message = this.configurationManagerService.getPreference('fiscalprinter.qr_code_message') || undefined;
        }

        printerDocumentData.itemsMap = await this.util.getItemsFromIds(clonedSale.sale_items);

        await this.handlePrintHooks('prePrintHook', clonedSale, pluginData, printerDocumentData);

        let result = await this.handleDocumentPlugins(clonedSale, pluginData, printerDocumentData);

        if (result.tail) {
            optionsPrint.tail = `${optionsPrint.tail ?? ''}${result.tail}`;
        }

        optionsPrint = {
            ...optionsPrint,
            ...result.options,
            otherDocuments: result.otherDocuments
        };

        // Assign device meta to sale
        const deviceMeta = await this.environmentInfo.getDeviceMeta();

        clonedSale.device_name = deviceMeta.device_name || undefined;
        clonedSale.device_serial = deviceMeta.client_uuid || undefined;

        try {
            if (['e_invoice', 'summary_e_rc', 'summary_e_nrc'].includes(printerDocumentData.document_type.id)) {
                const savedSale: Sales = await this.eInvoiceSend.sendEInvoice(structuredClone(clonedSale), printerDocumentData);

                try {
                    await this.handlePrintHooks('postPrintHook', structuredClone(savedSale), pluginData, printerDocumentData);
                } catch (err) {
                    //Nothing to do
                }

                //Resolve and handle receipt printing in the background
                return new Promise<Sales>(async (resolve) => {
                    resolve(structuredClone(savedSale));

                    /*
                        Receipt Printing is enabled only if e-receipt is not enabled and one of the following is true:
                        - Print e-invoice Receipt is enabled
                        - Print DDT is enabled
                        - Customer is private
                    */
                    const shouldPrintReceipt = (
                        !printerDocumentData?.options?.eReceipt &&
                        (
                            printerDocumentData?.options?.printEInvoiceReceipt ||
                            printerDocumentData?.options?.printDDT ||
                            (!!clonedSale.customer_tax_code || !clonedSale?.sale_customer?.company_name)
                        )
                    );

                    if (!shouldPrintReceipt) {
                        return;
                    }

                    const invoiceReceipt = savedSale.sale_documents?.find(document => document.document_type === 'receipt');

                    if (!invoiceReceipt?.document_content) {
                        return;
                    }

                    let success = false;

                    try {
                        const printCopies = parseInt(this.configurationManagerService.getPreference('fiscalprinter.e_invoice_receipt_copies') as string) || 1;

                        for (let i = 0; i < printCopies; i++) {
                            await this.printFreeNonFiscal(invoiceReceipt.document_content, printerDocumentData.printer.id!, { printHeader: false });
                        }

                        success = true;
                    } catch (err) {
                        //Nothing to do
                    } finally {
                        this.$rootScope.$broadcast('documentPrinter:document-printed', { type: 'e_invoice', success: success, saleData: structuredClone(savedSale) });
                    }
                });
            } else {
                let printService;

                switch (printerData.printer.type) {
                    case 'fiscal': case 'rt':
                        printService = this.FiscalPrinters;
                        break;
                    case 'receipt':
                        printService = this.NoFiscalPrinters;
                        break;
                    default:
                        throw 'INVALID_PRINTER_TYPE';
                }

                // Send a copy of the sale to the printing layer
                const fiscalSale = structuredClone(clonedSale);

                //Check if the user wants to remove the items with price 0
                if (this.configurationManagerService.getPreference('cashregister.remove_zero_price_items_from_receipt')) {
                    fiscalSale.sale_items = (fiscalSale.sale_items || []).filter(item => item.price !== 0 || item.final_price !== 0);
                }

                this.saleUtilsService.calculateSalePrices(fiscalSale);

                if(optionsPrint.print_details || this.fiscalUtils.isRefundVoidSale(fiscalSale)) {
                    fiscalSale.sale_items = this.fiscalUtils.extractSaleItems(fiscalSale);
                } else {
                    fiscalSale.sale_items = this.fiscalUtils.extractDepSaleItems(fiscalSale, { itemName: departmentItemName });
                    fiscalSale.price_changes = [];
                    this.saleUtilsService.calculateSalePrices(fiscalSale);
                }

                let [success, printedDocuments] = await printService.printDocument(fiscalSale, printerDocumentData, printerData, optionsPrint);

                for (const document of printedDocuments) {
                    Object.assign(document, {
                        printer_id: printerDocumentData.printer.id,
                        printer_name: printerDocumentData.printer.name
                    });
                }

                Object.assign(clonedSale, {
                    is_summary: ['summary_invoice', 'summary_e_rc', 'summary_e_nrc'].includes(printerDocumentData.document_type.id),
                    sale_documents: structuredClone(printedDocuments)
                });

                deepClone(clonedSale, origSale);

                try {
                    await this.handlePrintHooks('postPrintHook', clonedSale, pluginData, printerDocumentData);
                } catch (err) {
                    //Nothing to do
                }

                return new Promise<SalesDocuments[]>(async (resolve) => {
                    resolve(printedDocuments);

                    this.$rootScope.$broadcast('documentPrinter:document-printed', { type: 'other', success: success, saleData: clonedSale });

                    try {
                        await this.printSignatureCopy(clonedSale, printerData.printer.id!);
                    } catch (err) {
                        this.errorsLogger.err(`[ documentPrinter] failed to print signature copy for sale ${clonedSale.uuid}`);
                    }

                    if (['fiscal', 'rt'].includes(printerData.printer.type) && printerDocumentData?.options?.courtesyReceipt) {
                        try {
                            await this.FiscalPrinters.printCourtesyReceipt(clonedSale, printerData.printer.id!);
                        } catch (err) {
                            this.errorsLogger.err(`[ documentPrinter] failed to print courtesy receipt for ${printerDocumentData.document_type.id}`);
                        }
                    }
                });
            }
        } catch (errMessage: any) {
            let errors = [errMessage];

            try {
                await this.handlePrintHooks('printFailHook', clonedSale, pluginData, printerDocumentData);
            } catch (error) {
                errors.push(error);
            } finally {
                this.$rootScope.$broadcast('documentPrinter:document-print-failed', { error: errMessage, printerId: printerData.printer.id });

                throw errors;
            }
        }
    }

    public async sendSaleToPMS(sale: Sales) {
        const saleToSend = structuredClone(sale);

        for (const plugin of this.pmsPlugins) {
            if (plugin.isEnabled() && typeof plugin['postPrintHook'] === 'function') {
                await plugin['postPrintHook'](saleToSend, {
                    document_type: { id: 'generic_receipt' },
                    options: {},
                    printer: {} as Printers
                }).catch(() => {
                    //Nothing to do
                });
            }
        }
    }
}

angular.module('printers').service('documentPrinter', DocumentPrinterService);